import {
  useGetSearchTypeQuery,
  usePostSearchQuickMutation,
} from "../../../../redux/api/BaseApi";
import { useReduxDispatch, useReduxSelector } from "../../../../redux/hooks";
import {
  toggleQuickSearchingFlag,
  updateQuickSearchInput,
  updateQuickSearchResults,
} from "../../NavSlice";
import {
  AutoComplete,
  AutoCompleteChangeEvent,
  AutoCompleteCloseEvent,
  ListItemProps,
} from "@progress/kendo-react-dropdowns";
import React, {
  cloneElement,
  CSSProperties,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FlattenAndAddCategoryObjectsToQuickSearchResults } from "../../../../utils/utils";
import {
  ISearchQuickPost,
  ISearchQuickResults,
} from "../../../../types/ISearch";
import "./QuickSearch.css";
import { updateIframeSrc } from "../../../../slices/RevelationSlice";
import { debounce } from "lodash";
import useEventListener from "@use-it/event-listener";
import { TicketApi } from "../../../../redux/api/TicketApi";
import { useNavigate } from "react-router-dom";
import { UserApi } from "../../../../redux/api/UserApi";
import { Setting } from "../../../../types/Setting/Setting";

const QuickSearch = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetSearchTypeQuery();
  const getSearchTypeQuery = useGetSearchTypeQuery();
  const nv = useReduxSelector((state) => state.nav);
  const dispatch = useReduxDispatch();
  const [PostSearchQuickMutation] = usePostSearchQuickMutation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [findTicketId, setFindTicketId] = useState<number>();
  const { data: foundTicket, error: fetchTicketError } = TicketApi.useGetTicketQuery({ ticketId: findTicketId }, { skip: !findTicketId })
  const { data: newTicketPageSetting } = UserApi.useGetSettingQuery(Setting.NEW_TICKET_DETAILS);

  // performs the mutation
  async function postSearch(
    input: string,
    results: ISearchQuickResults[],
    filters: number[]
  ) {
    //console.log("made it to postSearch");
    if (
      !results.map((item) => item.Display).includes(input) &&
      input.length > 2
    ) {
      const post: ISearchQuickPost = {
        SearchTerm: input,
        SearchTypes: filters,
      };
      dispatch(toggleQuickSearchingFlag(true));
      //console.log("searching for ", input);
      await PostSearchQuickMutation(post)
        .unwrap()
        .then((payload) => {
          dispatch(
            updateQuickSearchResults(
              FlattenAndAddCategoryObjectsToQuickSearchResults(payload)
            )
          );
        })
        .catch((e) => {
          console.log(e);
        });
      dispatch(toggleQuickSearchingFlag(false));
      //.then(() => console.log("search complete"));
    }
  }
  /** handles Change event */
  const handleOnChange = (
    event: AutoCompleteChangeEvent | AutoCompleteCloseEvent
  ) => {
    //console.log('handleOnChange', typeof event.target.value, event.target.value?.trim());
    if (
      typeof event.target.value === "string" &&
      event.target.value.trim() !== ""
    ) {
      dispatch(updateQuickSearchInput(event.target.value));
      dispatch(updateQuickSearchResults([]));
      // checks for undefined due to clear all button
    } else if (!event.target.value) {
      dispatch(updateQuickSearchInput(""));
    } else {
      // should be object
      const chosen = event.target.value as unknown as ISearchQuickResults;
      if (!chosen.isCategory) {
        dispatch(updateQuickSearchInput(chosen.Display));
      }
    }
  };
  /** handle Item rendering */
  const handleItemRender = (
    li: ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const dataItem = itemProps.dataItem as ISearchQuickResults;
    const SharedStyles: CSSProperties = {
      marginLeft: "8px",
      textAlign: "left",
    };
    let itemChildren: JSX.Element;
    let liStyle: Partial<CSSStyleDeclaration>;
    if (dataItem.isCategory) {
      itemChildren = (
        <span
          style={{
            ...SharedStyles,
            fontSize: "1rem",
          }}
        >
          {dataItem.Display} ({dataItem.ResultCount})
        </span>
      );
      liStyle = { backgroundColor: "#ebebeb", border: "2px solid #469b42" };
    } else {
      itemChildren = (
        <button
          onClick={() => {
            //New Ticket details page
            if (newTicketPageSetting?.Bool_Value && dataItem.Url_Format?.toLocaleLowerCase().indexOf('callhistory.aspx') > -1) {
              navigate(`/ticket/${dataItem.Id}?from=quicksearch`)
              return;
            }

            navigate('/');
            dispatch(
              updateIframeSrc(
                `${dataItem.Url_Format.replace("={0}", `=${dataItem.Id}`)}`
              )
            )
          }
          }
          style={{ ...SharedStyles, fontSize: ".8rem" }}
        >
          {dataItem.Display.length > 70
            ? dataItem.Display.slice(0, 70) + "..."
            : dataItem.Display}
          {/*dataItem.Display*/}
        </button>
      );
      liStyle = { border: "1px solid #868586" };
    }
    const liProps = {
      ...li.props,
      style: {
        ...li.props.style,
        ...liStyle,
      },
    };
    return cloneElement(li, liProps, itemChildren);
  };
  /** handle search icon click */
  function handleSearchIconClick(searchPhrase: string, searchTypes: number[]) {
    console.log('handleSearchIconClick', encodeURIComponent(searchPhrase), 'searchTypes', searchTypes, 'selectedTypes', selectedTypes)

    const ticketStatusTypes = searchTypes.indexOf(-2) > -1 ? 'Ticket_Status_Type=0,1,2,4;0;1;2;3;4' : 'Ticket_Status_Type=1,2;1;2';
    const searchDefault = `/Search/Default.aspx?RunSearch=True&SearchTypes=${searchTypes}&Filters=Asset_Active=1~!~User_Status=1~!~Client_Status=2~!~Project_Status=1;3,1;3~!~${ticketStatusTypes}&SearchPhrase=${encodeURIComponent(searchPhrase)}`;
    dispatch(updateIframeSrc(`${searchDefault}`));
  }
  /** Optimize searching with filters. Filters are much less likely to change and thus don't need to be recomputed unless the filters change. */
  const searchFilters = useMemo(() => {
    if (data) {
      return data
        .filter((item) => {
          return item.Checked;
        })
        .map((item) => {
          return item.ID;
        });
    }
  }, [data]);
  /** Don't make a POST mutation until the user stops typing after x amount of milliseconds */
  const debouncePostSearch = useCallback(debounce(postSearch, 300), []);
  /** useEffect responsible for debouncing user input and then calling the Post mutation */
  useEffect(() => {
    debouncePostSearch(
      nv.quickSearchInput,
      nv.quickSearchResults,
      searchFilters
    );
  }, [nv.quickSearchInput]);

  ///Attach an event handler to the input field to listen for ENTER
  const autocomplete = useRef<any>();
  useEffect(() => {
    if (autocomplete && autocomplete?.current) {
      let input = autocomplete?.current?.element.querySelector('.k-input-inner')
      console.log('autocomplete input', input)
      if (input)
        input.addEventListener('keydown', handleKeyDown)
    }
  }, [autocomplete?.current])

  const selectedTypes = useMemo(() => getSearchTypeQuery.data && getSearchTypeQuery.data
    .filter((item) => item.Checked === true && item.ID !== 0)
    .map((item) => item.ID), [getSearchTypeQuery.data]);

  //Go directly to the ticket if you paste in the ticket number and press enter.
  function handleKeyDown(e) {
    if (e.keyCode === 13 && e.target.value) {
      //console.log('handleKeyDown', e.target.value, getSearchTypeQuery.data)
      let tid = parseInt(e.target.value.trim());
      Number.isNaN(tid) ? handleSearchIconClick(e.target.value, selectedTypes) : setFindTicketId(tid);
    }
  }
  useEffect(() => {
    //Go to the ticket if found
    console.log('Go to the ticket if found', findTicketId, foundTicket, fetchTicketError)
    if (findTicketId && foundTicket?.ID === findTicketId) {
      //dispatch(updateIframeSrc(`/Calls/CallHistory.aspx?Call_Id=${foundTicket.ID}`));

      setFindTicketId(null);
      //console.warn('Go to the ticket if found', findTicketId, foundTicket)
      navigate(`/deeplink?url=/Calls/CallHistory.aspx?Call_Id=${foundTicket.ID}`);
    }
    else if (fetchTicketError)
      handleSearchIconClick(nv.quickSearchInput, selectedTypes)
  }, [findTicketId, foundTicket, fetchTicketError])

  useEventListener("resize", () => setWindowWidth(window.innerWidth));
  let searchBarWidth = windowWidth > 768 ? "256px" : "128px";
  const autoCompleteStyle: CSSProperties = {
    width: searchBarWidth,
    border: "1px solid #868586",
    borderRadius: "0px",
    borderBottom: "2px solid #868586",
    height: "30px",
    backgroundColor: "#868586",
    color: "#f0f0f0",
  };

  if (error) return <div>Error</div>;

  if (isLoading) {
    return (
      <>
        <button onClick={() => console.log("Loading")}>
          <img
            className="bg-[#868586] grid place-content-center p-1"
            src={process.env.PUBLIC_URL + "/images/Nav/search.png"}
            width={30}
            alt="search bar"
          />
        </button>
        <AutoComplete style={autoCompleteStyle} />
      </>
    );
  }

  return (
    <>
      <button
        onClick={() =>
          handleSearchIconClick(
            nv.quickSearchInput,
            getSearchTypeQuery.data
              .filter((item) => item.Checked === true && item.ID !== 0)
              .map((item) => item.ID)
          )
        }
      >
        <img
          className="bg-[#868586] grid place-content-center p-1"
          src={process.env.PUBLIC_URL + "/images/Nav/search.png"}
          width={30}
          alt="search bar"
        />
      </button>
      <AutoComplete
        ref={autocomplete}
        style={autoCompleteStyle}
        data={nv.quickSearchResults}
        onChange={handleOnChange}
        itemRender={handleItemRender}
        loading={nv.quickSearchingFlag}
        value={nv.quickSearchInput}
        suggest={false}
        listNoDataRender={() => cloneElement(<div></div>)}
        popupSettings={{
          width: "500px",
          height: "500px",
          popupClass: "quickSearch",
        }}
        onClose={handleOnChange}
        footer={<AutoCompleteFooter props={nv.quickSearchResults} />}
      />
    </>
  );
};

/** Footer for Auto Complete */
const AutoCompleteFooter = ({ props }: { props: ISearchQuickResults[] }) => {
  const noCategoriesArrayLength = props.filter((res) => !res.isCategory).length;
  return (
    <span className="grid place-content-center bg-rev-green text-white">
      {noCategoriesArrayLength} Items Found
    </span>
  );
};

export default QuickSearch;
